import { useCallback, useMemo, useState } from 'react';
import useAlerts from '../useAlerts';
import { useQzTrayPrint } from '../qztray/useQzTrayPrint';
import { DesktopPrintTransport } from '../../services/printing/desktop-print.transport';
import { mapPrintError } from '../../services/printing/print-error.mapper';
import { PrintService } from '../../services/printing/print-service';
import { PrintTransportSelector } from '../../services/printing/print-transport.selector';
import { QzPrintTransport } from '../../services/printing/qz-print.transport';
import { DesktopDocumentType, PrintRequest, PrintResult } from '../../services/printing/printing.types';
import { QzTrayDocumentTypeCode } from '../../types/qztray/QzTrayDocumentTypes';

const legacyToDesktop: Partial<Record<QzTrayDocumentTypeCode, DesktopDocumentType>> = {
  SALE: 'SALE_TICKET',
  SALE_REPRINT: 'SALE_TICKET',
  PURCHASE: 'PURCHASE_TICKET',
  CASH_OPENING: 'CASH_OPENING',
  CASH_X: 'CASH_X',
  CASH_Z: 'CASH_Z',
  CASH_MOVEMENT: 'CASH_MOVEMENT',
};

const desktopToLegacy: Record<DesktopDocumentType, QzTrayDocumentTypeCode> = {
  SALE_TICKET: 'SALE',
  SALE_INVOICE: 'SALE',
  PURCHASE_TICKET: 'PURCHASE',
  CASH_OPENING: 'CASH_OPENING',
  CASH_X: 'CASH_X',
  CASH_Z: 'CASH_Z',
  CASH_MOVEMENT: 'CASH_MOVEMENT',
};

export const usePrintService = () => {
  const { setPermission } = useQzTrayPrint();
  const { show: showAlert } = useAlerts();
  const [isPrinting, setIsPrinting] = useState(false);

  const service = useMemo(() => {
    const qz = new QzPrintTransport((request) => {
      setPermission({
        allowPrint: true,
        id: request.documentId,
        type: desktopToLegacy[request.documentType],
      });
    });
    return new PrintService(new PrintTransportSelector(new DesktopPrintTransport(), qz));
  }, [setPermission]);

  const print = useCallback(async (request: PrintRequest): Promise<PrintResult> => {
    setIsPrinting(true);
    try {
      const result = await service.print(request);
      if (result.transport === 'desktop') {
        showAlert(
          'success',
          result.duplicate ? 'Documento ya procesado' : 'Impresión enviada',
          result.duplicate
            ? 'Codenova Desktop reconoció este trabajo como duplicado.'
            : `Documento enviado a ${result.printerName || 'la impresora configurada'}.`
        );
      }
      return result;
    } catch (error) {
      const mapped = mapPrintError(error);
      showAlert('error', mapped.title, mapped.message);
      throw error;
    } finally {
      setIsPrinting(false);
    }
  }, [service, showAlert]);

  const requestLegacyPrint = useCallback(async (
    type: QzTrayDocumentTypeCode,
    documentId: number
  ): Promise<PrintResult> => {
    const documentType = legacyToDesktop[type];
    if (!documentType) {
      setPermission({ allowPrint: true, id: documentId, type });
      return { transport: 'qz', copies: 1 };
    }
    return print({ documentType, documentId });
  }, [print, setPermission]);

  return { print, requestLegacyPrint, isPrinting };
};
